import React, { Component } from "react";
import { AxiosResponse } from "axios";
import CheckoutForm from "./CheckoutForm";
import OrderSummary from "./OrderSummary";
import ModalStore from "../../stores/ModalStore";
import TermsAndConditions from "../TermsAndConditions";
import { Props, State } from "./Types";
import { getRequest } from "../../utils/httpRequest";
import { formatCourses, formatCountries, canShowCoursePaymentToggle } from "./service";
import { scrollIntoView } from "../../utils/helpers";
import { Orbit } from "lucide-react";
import CheckoutIcons from "./CheckoutIcons";

const hasCompanyFields = (companyDetails) =>
  ["billingName", "address", "city", "state", "postalCode", "vatNumber"].some(
    (key) => companyDetails?.[key]?.trim(),
  );

class Checkout extends Component<Props, State> {
  modalStore = new ModalStore();

  // Guards a one-time check, so it is an instance field rather than state:
  // re-running it after the reader cancels a voucher would wrongly report the
  // code from the URL as rejected.
  urlCouponChecked = false;

  // Every request whose answer replaces the quote takes the next number, and
  // only the answer to the newest one is applied. Otherwise a slow earlier
  // answer, such as the reload when the currency arrives, could land after a
  // voucher was taken off and put it back.
  latestQuoteRequest = 0;

  requestQuote = (url: string) => {
    const ticket = ++this.latestQuoteRequest;
    return getRequest(url).then((response: AxiosResponse) => ({
      response,
      current: ticket === this.latestQuoteRequest,
    }));
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedCourse: null,
      isCourseError: false,
      step: "plan",
      kind: "",
      courses: [],
      priceable: null,
      activeQuote: null,
      quotes: [],
      country: "",
      countries: [],
      billingType: "user",
      couponCode: "",
      rejectedCoupon: "",
      refCode: props.refCode || "",
      category: "",
      monthlySubsequentPayment: "",
      monthlyPlanTotal: "",
      communityUpgradeAmount: "",
      userDetails: {
        name: "",
        email: "",
        address: "",
        city: "",
        postalCode: "",
        country: "",
      },
      companyDetails: {
        name: "",
        email: "",
        billingName: "",
        address: "",
        city: "",
        postalCode: "",
        vatNumber: "",
        vatType: "",
        country: "",
      },
      loading: false,
      loadingSubscription: true,
      canOpenTermsModal: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.modalStore.ready();
    this.syncBodyPlan();
    this.loadCountries();

    const { paymentUrl, slug, country } = this.props;
    if (this.props.paymentType === "course") {
      if (slug) {
        this.requestQuote(`/api/v2/courses/${slug}/pay?code=${this.props.couponCode}`)
          .then(({ response, current }) => {
            const selectedCourse = slug;
            this.setState({
              courses: response.data.courses,
              selectedCourse,
              country: country,
            });

            if (current) this.updateStateFromResponse(response.data);
            if (response.data.priceable.sample) {
              this.setState({ step: "payment" });

              const element = document.getElementById("card-details");
              scrollIntoView(element);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        getRequest("/api/courses/ranked")
          .then((response: AxiosResponse) => {
            const selectedCourse = slug;
            this.setState({
              courses: response.data,
              selectedCourse,
              country: country,
              loadingSubscription: false,
              loading: false,
            });
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } else {
      this.requestQuote(paymentUrl)
        .then(({ response, current }) => {
          if (current) this.updateStateFromResponse(response.data);
        })
        .catch((error) => {
          this.setState({ loading: false });
        });
    }
  }

  loadCountries = () => {
    return getRequest("/api/checkout/countries")
      .then((response: AxiosResponse) => {
        this.setState({ countries: response.data });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  componentDidUpdate(prevProps) {
    this.modalStore.ready();
    this.syncBodyPlan();
    const { paymentUrl, selectedCurrency, country, paymentType } = this.props;
    if (
      paymentUrl !== prevProps.paymentUrl ||
      selectedCurrency !== prevProps.selectedCurrency
    ) {
      // The currency often arrives before the first load has said which
      // course this is; the link's own course will do. With no course at all
      // (the pick-a-course checkout) there is nothing to reload, and asking
      // for course "null" only got a 500.
      const course = this.state.selectedCourse || this.props.slug;
      if (paymentType === "course" && !course) return;

      // The voucher in play goes with it, or the reload quietly drops one
      // that came in on the link (?coupon_code=). Until the first load has
      // answered, that is the link's own code; after it, whatever the
      // checkout now holds, so a voucher taken off stays off.
      const code = this.state.selectedCourse
        ? this.state.couponCode
        : this.props.couponCode;

      this.setState({ loadingSubscription: true, loading: true, country });
      const url =
        paymentType === "course"
          ? `/api/v2/courses/${course}/pay?code=${encodeURIComponent(code || "")}`
          : paymentUrl;

      return this.requestQuote(url)
        .then(({ response, current }) => {
          if (current) this.updateStateFromResponse(response.data);
        })
        .catch((error) => {
          this.setState({
            loading: false,
            loadingSubscription: false,
            country: country,
          });
        });
    }
  }

  componentWillUnmount() {
    delete document.body.dataset.plan;
  }

  syncBodyPlan = () => {
    const { paymentType, slug } = this.props;
    const { priceable } = this.state;
    const isGiftPass = paymentType === "gift" && priceable?.type === "pass";

    if (slug === "lifetime" || priceable?.is_lifetime) {
      document.body.dataset.plan = "lifetime";
    } else if (slug === "community" || priceable?.is_community) {
      document.body.dataset.plan = "community";
    } else if (paymentType === "course" || priceable?.type === "course") {
      document.body.dataset.plan = "single";
    } else if (paymentType === "subscription" || isGiftPass) {
      document.body.dataset.plan = "all-access";
    } else {
      delete document.body.dataset.plan;
    }
  };

  refreshQuote = (
    couponCode = this.state.couponCode,
    refCode = this.state.refCode,
  ) => {
    const { slug } = this.props;

    const { activeQuote } = this.state;
    if (activeQuote?.id) {
      return this.requestQuote(
        `/api/checkout/quotes/${activeQuote?.id}?code=${encodeURIComponent(
          couponCode,
        )}&ref=${refCode}&is_renew=${slug === "renew"}`,
      ).then(({ response, current }) => {
        if (!current) return;
        this.updateStateFromResponse(response.data);

        if (
          response.data.active_quote.amount <= 0 &&
          this.state.step === "billing"
        ) {
          this.setState({ step: "payment" });
        }
      });
    }
  };

  applyVoucher = (couponCode, refCode) => {
    return new Promise<void>((resolve, reject) => {
      this.setState({ couponCode, refCode }, () => {
        const quoteRequest = this.refreshQuote(couponCode, refCode);

        if (quoteRequest) {
          quoteRequest.then(resolve).catch(reject);
        } else {
          resolve();
        }
      });
    });
  };

  refreshQuoteWithoutCoupon = (e) => {
    const { slug } = this.props;

    e.preventDefault();

    const { activeQuote } = this.state;
    this.setState({ couponCode: "", refCode: "" });
    if (activeQuote?.id) {
      return this.requestQuote(
        `/api/checkout/quotes/${activeQuote?.id}?remove_coupon=true&is_renew=${
          slug === "renew"
        }`,
      ).then(({ response, current }) => {
        if (current) this.updateStateFromResponse(response.data);
      });
    }
  };

  updateStateFromResponse = (data) => {
    const { couponCode } = this.props;
    const { userDetails, companyDetails } = this.state;
    const coursePaymentPlanQuote = data.quotes?.find(
      (quote) => quote?.course_payment_plan,
    );
    const fullPaymentQuote = data.quotes?.find(
      (quote) => quote && !quote.course_payment_plan,
    );
    const previousCourseMode = this.state.activeQuote?.course_payment_plan;
    const showPaymentToggle = canShowCoursePaymentToggle({
      priceable: data.priceable,
      fullPaymentQuote,
      paymentPlanQuote: coursePaymentPlanQuote,
    });
    const activeQuote =
      this.props.paymentType === "course" && !showPaymentToggle
        ? fullPaymentQuote || data.active_quote
        : this.props.paymentType === "course" && previousCourseMode === false
          ? fullPaymentQuote
          : this.props.paymentType === "course" &&
              previousCourseMode === undefined &&
              this.props.coursePaymentOption === "full"
            ? fullPaymentQuote
            : this.props.paymentType === "course" && coursePaymentPlanQuote
              ? coursePaymentPlanQuote
              : data.active_quote;

    const updatedUserDetails = {
      ...userDetails,
      name: activeQuote?.payer_name || userDetails.name,
      email: activeQuote?.payer_email || userDetails.email,
      country: activeQuote?.country,
    };

    const updatedCompanyDetails = {
      ...companyDetails,
      name: activeQuote?.payer_name || companyDetails.name,
      email: activeQuote?.payer_email || companyDetails.email,
      country: activeQuote?.country,
    };

    // A code the API cannot apply -- misspelt, expired, or out of claims --
    // is dropped without a word, and the reader is quietly charged full price.
    // That is how a pre-filled voucher link fails: silently, at the till.
    const requestedCoupon = this.props.couponCode;
    let { rejectedCoupon } = this.state;

    if (!this.urlCouponChecked && requestedCoupon) {
      this.urlCouponChecked = true;
      rejectedCoupon = activeQuote?.coupon_id ? "" : requestedCoupon;
    } else if (activeQuote?.coupon_id) {
      // Someone typed a working code in after the one from the URL failed.
      // Without this the warning sits there insisting they are paying full
      // price while the summary shows the discount they just earned.
      rejectedCoupon = "";
    }

    this.setState({
      country: activeQuote?.country,
      activeQuote: activeQuote,
      quotes: data.quotes,
      priceable: data.priceable,
      category: data.category,
      couponCode: activeQuote?.coupon_code || "",
      rejectedCoupon,
      kind: data.kind,
      loading: false,
      loadingSubscription: false,
      userDetails: updatedUserDetails,
      companyDetails: updatedCompanyDetails,
      monthlySubsequentPayment: data.monthly_subsequent_payment,
      monthlyPlanTotal: data.monthly_plan_total,
      communityUpgradeAmount: data.community_upgrade_amount,
    });
  };

  handleChange = (key, value) => {
    this.setState((prevState) => ({
      ...prevState,
      [key]: value,
      ...(key === "companyDetails" && hasCompanyFields(value)
        ? { billingType: "company" }
        : {}),
    }));
  };

  onCourseChange = (e, slug) => {
    const { couponCode, refCode } = this.state;

    this.setState({ loadingSubscription: true, selectedCourse: slug });

    return this.requestQuote(
      `/api/checkout/courses/${slug}?code=${this.props.couponCode || couponCode}&ref=${refCode}`,
    ).then(({ response, current }) => {
      if (current) this.updateStateFromResponse(response.data);
    });
  };

  render() {
    const { slug, path, paymentType, token, isCurrentUserPresent } = this.props;

    const {
      isCourseError,
      step,
      loading,
      courses,
      countries,
      priceable,
      country,
      selectedCourse,
      activeQuote,
      userDetails,
      companyDetails,
      billingType,
      couponCode,
      rejectedCoupon,
      refCode,
      quotes,
      category,
      monthlySubsequentPayment,
      monthlyPlanTotal,
      communityUpgradeAmount,
      kind,
      loadingSubscription,
      canOpenTermsModal,
    } = this.state;

    return (
      <>
        <CheckoutIcons />
        <main className="facelift-checkout">
          {rejectedCoupon && (
            <div className="checkout-coupon-warning" role="alert">
              <span className="checkout-coupon-warning-icon" aria-hidden="true">
                !
              </span>
              <span>
                We couldn't apply the code <strong>{rejectedCoupon}</strong>, so
                the price below is the full price. It may have expired or been
                fully claimed. Please check the link you were sent, or email{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>{" "}
                and we'll sort it out.
              </span>
            </div>
          )}
          <div className="checkout-wrap">
            <CheckoutForm
              slug={slug}
              token={token}
              kind={kind}
              billingType={billingType}
              activeQuote={activeQuote}
              priceable={priceable}
              monthlySubsequentPayment={monthlySubsequentPayment}
              monthlyTotalAmount={monthlyPlanTotal}
              communityUpgradeAmount={communityUpgradeAmount}
              quotes={quotes}
              courses={formatCourses(courses)}
              selectedCourse={selectedCourse}
              onCourseChange={this.onCourseChange}
              isCurrentUserPresent={isCurrentUserPresent}
              country={country}
              countries={formatCountries(countries)}
              handleChange={this.handleChange}
              userDetails={userDetails}
              companyDetails={companyDetails}
              paymentType={paymentType}
              couponCode={couponCode}
              refCode={refCode}
              applyVoucher={this.applyVoucher}
            />
            <OrderSummary
              paymentType={paymentType}
              slug={slug}
              path={path}
              isCourseError={isCourseError}
              priceable={priceable}
              course={selectedCourse}
              category={category}
              couponCode={couponCode}
              selectedCourse={selectedCourse}
              refCode={refCode}
              quotes={quotes}
              activeQuote={activeQuote}
              monthlySubsequentPayment={monthlySubsequentPayment}
              monthlyPlanTotal={monthlyPlanTotal}
              loading={loading}
              loadingSubscription={loadingSubscription}
              handleChange={this.handleChange}
              refreshQuote={this.refreshQuote}
              refreshQuoteWithoutCoupon={this.refreshQuoteWithoutCoupon}
            />
          </div>
        </main>
      </>
    );
  }
}

export default Checkout;
